import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import type {
    IPendingPreOpFormValues,
    IPendingPreOpModel,
    IPendingPreOpSubmitResult,
} from '@/types/surveys/pending-preop';

const PENDING_PREOP_ENDPOINT = '/api/register/pending-preop';

const defaultPendingPreOpModel: IPendingPreOpModel = {
    id: 0,
    editKey: '',
    hasBeenScheduled: false,
    scheduledDateTime: null,
    hasCompleted: null,
    treatment: '',
    convenience: '',
    facility: '',
    commendations: '',
    comments: '',
    languageData: {}        
};

export const usePendingPreOp = (registrationId: number, editKey: string) => {
    const url = `${PENDING_PREOP_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel<IPendingPreOpModel>(url, defaultPendingPreOpModel);

    const { submit, isSubmitting, submitError } = useApiSubmit<IPendingPreOpFormValues, DashRegistrationStep, IPendingPreOpSubmitResult>(
        url,
        (values) => {
            // The sub-questions only exist while their parent answer is Yes, so anything
            // behind a No is sent empty rather than as whatever was typed before.
            const hasBeenScheduled = toBoolOrNull(values.hasBeenScheduled);
            const hasCompleted = hasBeenScheduled === true ? toBoolOrNull(values.hasCompleted) : null;

            return {
                hasBeenScheduled,
                scheduledDateTime: hasBeenScheduled === true && values.scheduledDateTime !== '' ? values.scheduledDateTime : null,
                hasCompleted,
                treatment: hasCompleted === true ? toIntOrNull(values.treatment) : null,
                convenience: hasCompleted === true ? toIntOrNull(values.convenience) : null,
                facility: hasCompleted === true ? toIntOrNull(values.facility) : null,
                commendations: hasCompleted === true ? values.commendations : '',
                comments: hasCompleted === true ? values.comments : '',
            };
        },
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
